import { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

interface RetryConfig {
  maxRetries: number
  retryDelay: number
  retryableStatuses: number[]
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  retryCount?: number
}

// Only a read is sent again. A write that got no answer, or a 5xx, may
// already have been done by the server, and sending it again does it twice:
// on 2026-10-07 a resent final audit finalize closed three stores' audits with
// part of their count missing (web-portal#97).
const READ_METHODS = new Set(['get', 'head'])

/**
 * Whether a request only reads. Anything else, including a request whose method
 * is unknown, is treated as a write that may have been done.
 */
export function isReadRequest(config: { method?: string } | undefined): boolean {
  return READ_METHODS.has(config?.method?.toLowerCase() ?? '')
}

function shouldRetry(error: AxiosError, config: RetryConfig): boolean {
  if (!isReadRequest(error.config)) return false
  if (!error.response) return true

  return config.retryableStatuses.includes(error.response.status)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function setupRetryInterceptor(
  client: AxiosInstance,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const retryableConfig = error.config as RetryableConfig | undefined

      if (!retryableConfig) {
        return Promise.reject(error)
      }

      const currentRetryCount = retryableConfig.retryCount ?? 0

      if (currentRetryCount >= config.maxRetries) {
        return Promise.reject(error)
      }

      if (!shouldRetry(error, config)) {
        return Promise.reject(error)
      }

      retryableConfig.retryCount = currentRetryCount + 1

      const delayMs = config.retryDelay * Math.pow(2, currentRetryCount)
      await delay(delayMs)

      return client(retryableConfig)
    }
  )
}
